import React, { useMemo, useState } from 'react';

import type { RetrievalVerification } from '../../../types/retrieval';

interface VerificationBadgeProps {
  verification: RetrievalVerification;
}

export const VerificationBadge: React.FC<VerificationBadgeProps> = ({ verification }) => {
  const [expanded, setExpanded] = useState(false);
  const formatRuleName = (raw: string) =>
    raw.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  const failingChecks = verification.checks.filter((item) => !item.passed);

  const summary = useMemo(() => {
    const warningCount = failingChecks.filter((item) => item.severity === 'warning').length;
    const errorCount = failingChecks.filter((item) => item.severity === 'error').length;
    if (errorCount > 0) {
      return `${errorCount} error${errorCount > 1 ? 's' : ''}, ${warningCount} warning${warningCount !== 1 ? 's' : ''}`;
    }
    if (warningCount > 0) {
      return `${warningCount} warning${warningCount > 1 ? 's' : ''}`;
    }
    return 'No verification issues';
  }, [failingChecks]);

  const toneClass = verification.passed
    ? 'border-green-200 bg-green-50 text-green-700'
    : 'border-amber-200 bg-amber-50 text-amber-700';

  return (
    <div className={`rounded-lg border p-2 ${toneClass}`}>
      <button
        type="button"
        className="flex w-full items-center justify-between text-left text-xs font-semibold"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span>{verification.summary || summary}</span>
        <span className="text-[10px]">{expanded ? 'Hide' : 'Details'}</span>
      </button>
      {expanded ? (
        <div className="mt-2 space-y-1.5 rounded border border-neutral-200 bg-white p-2 text-xs text-neutral-700">
          {failingChecks.length > 0 ? (
            failingChecks.map((check, index) => (
              <div key={`${check.ruleName}-${index}`}>
                <p className="font-medium text-neutral-900">{formatRuleName(check.ruleName)}</p>
                <p>{check.message}</p>
              </div>
            ))
          ) : (
            <p>All verification checks passed.</p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default VerificationBadge;
